import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SEO from '../../components/SEO';
import SidebarNavigation from './components/SidebarNavigation';
import SearchBar from './components/SearchBar';
import ContentSection from './components/ContentSection';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../utils/supabase';
import authService from '../../utils/authService';
import { getAuthRedirectPaths } from '../../utils/authRedirect';
import FAQ from '../../components/ui/FAQ';
import { getFAQsForPage } from '../../utils/faqContent';
import { generatePageFAQSchema } from '../../utils/faqSchema';

const ApiDocumentation = () => {
  // Get FAQs for API documentation page
  const apiFAQs = getFAQsForPage('api');
  const faqSchema = generatePageFAQSchema('api', 'https://mailvalidator.octalchip.com/docs', {
    pageTitle: 'API Documentation - Email Validator',
    pageDescription: 'Complete API documentation for email validation. Learn how to integrate email validation into your applications with our RESTful API.'
  });
  
  const [activeSection, setActiveSection] = useState('getting-started-overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [bearerToken, setBearerToken] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const [showCopiedCurl, setShowCopiedCurl] = useState(false);
  const [showCopiedSingle, setShowCopiedSingle] = useState(false);
  const [showCopiedBulk, setShowCopiedBulk] = useState(false);
  const [showCopiedCredits, setShowCopiedCredits] = useState(false);
  const [showCopiedApiKeyCurl, setShowCopiedApiKeyCurl] = useState(false);
  const [showCopiedApiKeySingle, setShowCopiedApiKeySingle] = useState(false);
  const [showCopiedApiKeyBulk, setShowCopiedApiKeyBulk] = useState(false);
  const [showCopiedApiKeyCredits, setShowCopiedApiKeyCredits] = useState(false);
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginPath } = getAuthRedirectPaths(location);

  // API Key Management State
  const [apiKeyData, setApiKeyData] = useState({
    hasApiKey: false,
    apiKeyMasked: null,
    generatedKey: null,
    showGeneratedKey: false
  });
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isRevokingKey, setIsRevokingKey] = useState(false);

  // Load API key status on component mount
  useEffect(() => {
    if (user?.id) {
      loadApiKeyStatus();
    }
  }, [user?.id]);

  const loadApiKeyStatus = async () => {
    try {
      const result = await authService.getUserApiKey(user.id);
      if (result.success) {
        setApiKeyData(prev => ({
          ...prev,
          hasApiKey: result.data.hasApiKey,
          apiKeyMasked: result.data.apiKeyMasked
        }));
      }
    } catch (error) {
      console.error('Error loading API key status:', error);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!user?.id) {
      showSuccess('Login is required to generate or manage your API key. Redirecting you to the login page...');
      navigate(loginPath);
      return;
    }

    setIsGeneratingKey(true);
    try {
      const result = await authService.createApiKey(user.id);
      if (result.success) {
        setApiKeyData(prev => ({
          ...prev,
          hasApiKey: true,
          generatedKey: result.data.apiKey,
          showGeneratedKey: true,
          apiKeyMasked: `evapi${'*'.repeat(32)}`
        }));
        showSuccess('API key generated successfully!');
      } else {
        showError(result.error || 'Failed to generate API key');
      }
    } catch (error) {
      showError('An unexpected error occurred');
      console.error('API key generation error:', error);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!user?.id) {
      showSuccess('Login is required to manage your API key. Redirecting you to the login page...');
      navigate(loginPath);
      return;
    }

    if (!window.confirm('Are you sure you want to revoke your API key? This will immediately invalidate the key and any integrations using it will stop working.')) {
      return;
    }

    setIsRevokingKey(true);
    try {
      const result = await authService.revokeApiKey(user.id);
      if (result.success) {
        setApiKeyData(prev => ({
          ...prev,
          hasApiKey: false,
          generatedKey: null,
          showGeneratedKey: false,
          apiKeyMasked: null
        }));
        showSuccess('API key revoked successfully!');
      } else {
        showError(result.error || 'Failed to revoke API key');
      }
    } catch (error) {
      showError('An unexpected error occurred');
      console.error('API key revocation error:', error);
    } finally {
      setIsRevokingKey(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('API key copied to clipboard!');
    } catch (error) {
      showError('Failed to copy to clipboard');
    }
  };

  // Close sidebar on mobile when section changes
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  }, [activeSection]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get user's Bearer token
  useEffect(() => {
    const getBearerToken = async () => {
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setBearerToken(session.access_token);
        }
      }
    };

    getBearerToken();
  }, [user]);

  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      setIsSearching(true);
      
      // Simulate search functionality
      setTimeout(() => {
        const mockResults = [
          {
            id: 'getting-started-overview',
            title: 'API Overview',
            content: 'EmailValidator API documentation and getting started guide',
            section: 'Getting Started'
          },
          {
            id: 'authentication-api-keys',
            title: 'API Authentication',
            content: 'How to authenticate with API keys and security best practices',
            section: 'Authentication'
          },
          {
            id: 'endpoints-single-validation',
            title: 'Single Email Validation',
            content: 'Validate individual email addresses with comprehensive results',
            section: 'API Endpoints'
          }
        ].filter(result => 
          result.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          result.content.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setSearchResults(mockResults);
        setIsSearching(false);
      }, 300);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setSearchResults([]);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleCopyToken = () => {
    const tokenToCopy = bearerToken ? `Bearer ${bearerToken}` : 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    navigator.clipboard.writeText(tokenToCopy);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const handleCopyCurl = () => {
    const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
    navigator.clipboard.writeText(curlCommand);
    setShowCopiedCurl(true);
    setTimeout(() => setShowCopiedCurl(false), 2000);
  };

  const handleCopySingle = () => {
    const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["john.doe@example.com"],
    "validationType": "single"
}'`;
    navigator.clipboard.writeText(curlCommand);
    setShowCopiedSingle(true);
    setTimeout(() => setShowCopiedSingle(false), 2000);
  };

  const handleCopyBulk = () => {
    const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
    navigator.clipboard.writeText(curlCommand);
    setShowCopiedBulk(true);
    setTimeout(() => setShowCopiedBulk(false), 2000);
  };

  const handleCopyCredits = () => {
    const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [""],
    "validationType": "bulk"
}'`;
    navigator.clipboard.writeText(curlCommand);
    setShowCopiedCredits(true);
    setTimeout(() => setShowCopiedCredits(false), 2000);
  };

  // API Key specific copy functions
  const handleCopyApiKeyCurl = async () => {
    if (!apiKeyData.hasApiKey) {
      showError('Please generate an API key first');
      return;
    }
    
    try {
      // If we have the generated key in memory, use it
      if (apiKeyData.generatedKey) {
        const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${apiKeyData.generatedKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
        navigator.clipboard.writeText(curlCommand);
        setShowCopiedApiKeyCurl(true);
        setTimeout(() => setShowCopiedApiKeyCurl(false), 2000);
      } else {
        // If not in memory, fetch from database
        const result = await authService.getActualApiKey(user.id);
        if (result.success && result.data.apiKey) {
          const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${result.data.apiKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
          navigator.clipboard.writeText(curlCommand);
          setShowCopiedApiKeyCurl(true);
          setTimeout(() => setShowCopiedApiKeyCurl(false), 2000);
        } else {
          showError('Unable to retrieve API key. Please regenerate your API key.');
        }
      }
    } catch (error) {
      showError('Failed to copy cURL command');
      console.error('Copy error:', error);
    }
  };

  const handleCopyApiKeySingle = async () => {
    if (!apiKeyData.hasApiKey) {
      showError('Please generate an API key first');
      return;
    }
    
    try {
      // If we have the generated key in memory, use it
      if (apiKeyData.generatedKey) {
        const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${apiKeyData.generatedKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["john.doe@example.com"],
    "validationType": "single"
}'`;
        navigator.clipboard.writeText(curlCommand);
        setShowCopiedApiKeySingle(true);
        setTimeout(() => setShowCopiedApiKeySingle(false), 2000);
      } else {
        // If not in memory, fetch from database
        const result = await authService.getActualApiKey(user.id);
        if (result.success && result.data.apiKey) {
          const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${result.data.apiKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["john.doe@example.com"],
    "validationType": "single"
}'`;
          navigator.clipboard.writeText(curlCommand);
          setShowCopiedApiKeySingle(true);
          setTimeout(() => setShowCopiedApiKeySingle(false), 2000);
        } else {
          showError('Unable to retrieve API key. Please regenerate your API key.');
        }
      }
    } catch (error) {
      showError('Failed to copy cURL command');
      console.error('Copy error:', error);
    }
  };

  const handleCopyApiKeyBulk = async () => {
    if (!apiKeyData.hasApiKey) {
      showError('Please generate an API key first');
      return;
    }
    
    try {
      // If we have the generated key in memory, use it
      if (apiKeyData.generatedKey) {
        const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${apiKeyData.generatedKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
        navigator.clipboard.writeText(curlCommand);
        setShowCopiedApiKeyBulk(true);
        setTimeout(() => setShowCopiedApiKeyBulk(false), 2000);
      } else {
        // If not in memory, fetch from database
        const result = await authService.getActualApiKey(user.id);
        if (result.success && result.data.apiKey) {
          const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${result.data.apiKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`;
          navigator.clipboard.writeText(curlCommand);
          setShowCopiedApiKeyBulk(true);
          setTimeout(() => setShowCopiedApiKeyBulk(false), 2000);
        } else {
          showError('Unable to retrieve API key. Please regenerate your API key.');
        }
      }
    } catch (error) {
      showError('Failed to copy cURL command');
      console.error('Copy error:', error);
    }
  };

  const handleCopyApiKeyCredits = async () => {
    if (!apiKeyData.hasApiKey) {
      showError('Please generate an API key first');
      return;
    }
    
    try {
      // If we have the generated key in memory, use it
      if (apiKeyData.generatedKey) {
        const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${apiKeyData.generatedKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [""],
    "validationType": "bulk"
}'`;
        navigator.clipboard.writeText(curlCommand);
        setShowCopiedApiKeyCredits(true);
        setTimeout(() => setShowCopiedApiKeyCredits(false), 2000);
      } else {
        // If not in memory, fetch from database
        const result = await authService.getActualApiKey(user.id);
        if (result.success && result.data.apiKey) {
          const curlCommand = `curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${result.data.apiKey}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [""],
    "validationType": "bulk"
}'`;
          navigator.clipboard.writeText(curlCommand);
          setShowCopiedApiKeyCredits(true);
          setTimeout(() => setShowCopiedApiKeyCredits(false), 2000);
        } else {
          showError('Unable to retrieve API key. Please regenerate your API key.');
        }
      }
    } catch (error) {
      showError('Failed to copy cURL command');
      console.error('Copy error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="API Documentation"
        description="Complete API documentation for EmailValidator - integrate email validation services into your applications with our REST API and SDKs."
        keywords="email validation API, REST API documentation, email verification SDK, developer integration, API reference, email validation endpoints, API documentation"
        canonical="https://mailvalidator.octalchip.com/api-documentation"
        structuredData={faqSchema}
      />

      <Header />
      
      <div className="mx-auto px-6">
        <Breadcrumb />
        
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between py-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <h1 className="text-xl font-semibold text-text-primary">API Documentation</h1>
          </div>
          <Button
            variant="ghost"
            onClick={toggleSidebar}
            iconName="Menu"
            iconSize={20}
            className="lg:hidden"
          >
            <span className="sr-only">Toggle navigation</span>
          </Button>
        </div>

        <div className="flex min-h-screen">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-20 h-[calc(100vh-5rem)]">
              <SidebarNavigation
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
              />
            </div>
          </div>

          {/* Mobile Sidebar Overlay */}
          {isSidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-300 flex">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={toggleSidebar} />
              <div className="relative flex flex-col w-80 max-w-xs bg-surface">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h2 className="text-lg font-semibold text-text-primary">Navigation</h2>
                  <Button
                    variant="ghost"
                    onClick={toggleSidebar}
                    iconName="X"
                    iconSize={20}
                  >
                    <span className="sr-only">Close navigation</span>
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <SidebarNavigation
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 lg:pl-8">
            <div className="py-8">
              {/* Search Bar */}
              <div className="mb-8">
                <SearchBar
                  onSearch={handleSearch}
                  placeholder="Search documentation..."
                />
              </div>

              {/* Authentication Section */}
              <div id="authentication" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="Key" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        Authentication Methods
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Choose between JWT Bearer tokens or API keys for authentication
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {/* JWT Bearer Token */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        JWT Bearer Token (for web apps):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-sm">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyToken}>
                            <span className="text-text-secondary">Bearer </span>
                            <span className="text-text-primary">
                              {bearerToken ? `${bearerToken.substring(0, 20)}...` : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'.substring(0, 20)}...`
                            </span>
                            {showCopied && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyToken}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="AlertTriangle" size={18} className="text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-800 mb-1">
                            Important Security Notice
                          </h4>
                          <p className="text-sm text-yellow-700">
                            Keep your Bearer token secure and never share it publicly. This token provides access to your account and API resources.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* API Key Management Section */}
              <div id="api-key-management" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="Key" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        API Key Management
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Generate and manage API keys for external integrations
                      </p>
                    </div>
                  </div>

                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={18} className="text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-blue-800 mb-1">
                          Login required
                        </h4>
                        <p className="text-sm text-blue-700">
                          You must be logged in to generate or manage your API key. If you are not signed in and try to generate or manage an API key, you will be redirected to the login page. After signing in, you can return here to create or manage your keys.
                        </p>
                      </div>
                    </div>
                  </div>

                  {apiKeyData.showGeneratedKey && apiKeyData.generatedKey && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-start space-x-3">
                        <Icon name="Key" size={20} className="text-green-600 mt-0.5" />
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-green-800 mb-2">
                              Your API Key (Save this now - it won't be shown again!)
                            </h4>
                            <div className="flex items-center space-x-2">
                              <Input
                                value={`ApiKey ${apiKeyData.generatedKey}`}
                                readOnly
                                className="font-mono text-sm bg-white"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyToClipboard(`ApiKey ${apiKeyData.generatedKey}`)}
                                iconName="Copy"
                                iconPosition="left"
                              >
                                Copy
                              </Button>
                            </div>
                          <p className="text-xs text-green-600 mt-2">
                            ⚠️ This is the only time you'll see this key. Save it securely!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {apiKeyData.hasApiKey && !apiKeyData.showGeneratedKey && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="flex items-center space-x-3">
                        <Icon name="Key" size={20} className="text-blue-600" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800">
                            API Key Active
                          </h4>
                          <p className="text-xs text-blue-600 mt-1">
                            {apiKeyData.apiKeyMasked}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-md">
                      <h4 className="text-sm font-medium text-text-primary mb-2">API Key Usage</h4>
                      <div className="text-sm text-text-secondary space-y-1">
                        <p>• Use <code className="bg-gray-200 px-1 rounded">Authorization: ApiKey YOUR_KEY</code> in API requests</p>
                        <p>• API keys have unlimited lifespan (until revoked)</p>
                        <p>• Perfect for external integrations and automation</p>
                        <p>• Keys are hashed and stored securely</p>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      {!apiKeyData.hasApiKey ? (
                        <Button
                          variant="primary"
                          onClick={handleGenerateApiKey}
                          loading={isGeneratingKey}
                          iconName="Key"
                          iconPosition="left"
                        >
                          Generate API Key
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            onClick={handleGenerateApiKey}
                            loading={isGeneratingKey}
                            iconName="RefreshCw"
                            iconPosition="left"
                          >
                            Regenerate Key
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={handleRevokeApiKey}
                            loading={isRevokingKey}
                            iconName="Trash2"
                            iconPosition="left"
                            className="text-error hover:text-error"
                          >
                            Revoke Key
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 rounded-md">
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium">Security Notice:</p>
                        <ul className="mt-1 list-disc list-inside space-y-1 text-xs">
                          <li>Keep your API key secure and never share it publicly</li>
                          <li>Regenerate your key if you suspect it's been compromised</li>
                          <li>API keys provide full access to your account</li>
                          <li>Use environment variables to store keys in production</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* cURL Command Section */}
              <div id="curl-examples" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="Terminal" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        cURL Command Examples for Bulk Emails
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Use these commands to test the API with Bearer token or API Key for bulk email validation
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command with Bearer Token (Bulk Emails):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyCurl}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedCurl && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyCurl}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* API Key cURL Command (Bulk Emails) */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command with API Key (Bulk Emails):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyApiKeyCurl}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey ${apiKeyData.generatedKey || (apiKeyData.hasApiKey ? 'YOUR_API_KEY_HERE (regenerate to get actual key)' : 'YOUR_API_KEY_HERE')}' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedApiKeyCurl && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyApiKeyCurl}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="Info" size={18} className="text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800 mb-1">
                            How to Use This Command
                          </h4>
                          <p className="text-sm text-blue-700 mb-3">
                            Copy this cURL command and paste it into your terminal to test the email validation API. The command includes your Bearer token and sample email data.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-md border">
                              <Icon name="Terminal" size={14} className="text-green-600" />
                              <span className="text-xs font-medium text-gray-700">Terminal</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-md border">
                              <Icon name="Globe" size={14} className="text-orange-600" />
                              <span className="text-xs font-medium text-gray-700">Postman</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-md border">
                              <Icon name="Code" size={14} className="text-blue-600" />
                              <span className="text-xs font-medium text-gray-700">cURL</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-md border">
                              <Icon name="FileText" size={14} className="text-purple-600" />
                              <span className="text-xs font-medium text-gray-700">Insomnia</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Single Email Validation Section */}
              <div id="single-email-validation" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="Mail" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        Single Email Validation
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Validate a single email address with detailed results
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command (Single Email):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopySingle}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken ? `${bearerToken.substring(0, 20)}...` : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'.substring(0, 20)}...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["john.doe@example.com"],
    "validationType": "single"
}'`}
                            </div>
                            {showCopiedSingle && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopySingle}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* API Key cURL Command (Single Email) */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command with API Key (Single Email):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyApiKeySingle}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey evapia1b2c3...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": ["john.doe@example.com"],
    "validationType": "single"
}'`}
                            </div>
                            {showCopiedApiKeySingle && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyApiKeySingle}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={18} className="text-green-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800 mb-1">
                            Single Email Validation
                          </h4>
                          <p className="text-sm text-green-700">
                            Perfect for validating individual email addresses. Returns detailed validation results including syntax, domain, and deliverability checks.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bulk Email Validation Section */}
              <div id="bulk-email-validation" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        Bulk Email Validation
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Validate multiple email addresses in a single request
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command (Bulk Emails):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyBulk}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken ? `${bearerToken.substring(0, 20)}...` : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'.substring(0, 20)}...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedBulk && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyBulk}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* API Key cURL Command (Bulk Emails) */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command with API Key (Bulk Emails):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyApiKeyBulk}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey evapia1b2c3...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [
        "john.doe@example.com",
        "jane.smith@company.org",
        "contact@business.net",
        "support@website.com",
        "info@organization.edu"
    ],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedApiKeyBulk && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyApiKeyBulk}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="Zap" size={18} className="text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800 mb-1">
                            Bulk Email Validation
                          </h4>
                          <p className="text-sm text-blue-700">
                            Ideal for processing large email lists. Validates multiple emails efficiently and returns comprehensive results for each email address.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Credit Details Section */}
              <div id="credit-details" className="mb-8">
                <div className="bg-gradient-to-r from-primary-light to-white border border-primary/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="CreditCard" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        Get Credit Details
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Check your credit balance and validation statistics without consuming credits
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command (Credit Details):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyCredits}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: Bearer ${bearerToken ? `${bearerToken.substring(0, 20)}...` : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'.substring(0, 20)}...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [""],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedCredits && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyCredits}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* API Key cURL Command (Credit Details) */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        cURL Command with API Key (Credit Details):
                      </label>
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 bg-white border border-border rounded-md px-4 py-3 font-mono text-xs overflow-x-auto">
                          <div className="select-all cursor-pointer hover:bg-gray-50 transition-colors relative" onClick={handleCopyApiKeyCredits}>
                            <div className="whitespace-pre-wrap">
{`curl --location 'https://api.octalchip.com/emailvalidator' \\
--header 'Authorization: ApiKey evapia1b2c3...' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "emails": [""],
    "validationType": "bulk"
}'`}
                            </div>
                            {showCopiedApiKeyCredits && (
                              <div className="absolute -top-2 -right-2 bg-primary text-white text-xs px-2 py-1 rounded-md shadow-lg animate-pulse">
                                Copied!
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleCopyApiKeyCredits}
                          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="Copy" size={16} />
                          <span className="text-sm">Copy</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex items-start space-x-3">
                        <Icon name="Info" size={18} className="text-green-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800 mb-1">
                            Credit Details API
                          </h4>
                          <p className="text-sm text-green-700">
                            Get your current credit balance, validation statistics, and subscription plan details without consuming any credits. Perfect for checking your account status.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mb-8">
                  <div className="bg-surface border border-border rounded-lg p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Icon name="Search" size={20} className="text-primary" />
                      <h3 className="text-lg font-semibold text-text-primary">
                        Search Results ({searchResults.length})
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {searchResults.map((result) => (
                        <div
                          key={result.id}
                          className="p-4 border border-border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                          onClick={() => handleSectionChange(result.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-text-primary mb-1">
                                {result.title}
                              </h4>
                              <p className="text-sm text-text-secondary mb-2">
                                {result.content}
                              </p>
                              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                {result.section}
                              </span>
                            </div>
                            <Icon name="ChevronRight" size={16} className="text-text-secondary ml-4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isSearching && (
                <div className="mb-8 flex items-center justify-center py-8">
                  <div className="flex items-center space-x-2">
                    <Icon name="Loader" size={20} className="text-primary animate-spin" />
                    <span className="text-text-secondary">Searching documentation...</span>
                  </div>
                </div>
              )}

              {/* Documentation Content */}
              {!isSearching && searchResults.length === 0 && (
                <div id="api-documentation-content">
                  <ContentSection activeSection={activeSection} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Floating Button */}
      <div className="fixed bottom-6 right-6 z-200">
        <div className="flex flex-col space-y-2">
          <Button
            variant="primary"
            className="rounded-full w-12 h-12 shadow-lg p-0 flex items-center justify-center"
            iconName="ArrowUp"
            iconSize={20}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top"
          />
        </div>
      </div>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50 mt-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about using our email validation API
            </p>
          </div>
          <FAQ items={apiFAQs} variant="default" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-16">
        <div className="mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-md">
                  <Icon name="Mail" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-text-primary font-heading">
                  EmailValidator
                </span>
              </div>
              <p className="text-sm text-text-secondary">
                Professional email validation API for businesses and developers.
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><a href="/about" className="hover:text-primary transition-colors">About Us</a></li>
                <li><a href="/contact" className="hover:text-primary transition-colors">Contact Us</a></li>
                <li><a href="/pricing" className="hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="/api-documentation" className="hover:text-primary transition-colors">API Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><a href="/api-documentation" className="hover:text-primary transition-colors">API Documentation</a></li>
                <li><a href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-primary transition-colors">Terms of Service</a></li>
                <li><a href="/cancellation-refund" className="hover:text-primary transition-colors">Cancellation & Refund</a></li>
                <li><a href="/shipping-delivery" className="hover:text-primary transition-colors">Service Delivery</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><a href="/contact" className="hover:text-primary transition-colors">Contact Us</a></li>
                <li><a href="/contact#contact-form" className="hover:text-primary transition-colors">Support</a></li>
                <li><a href="/contact#contact-information" className="hover:text-primary transition-colors">Help Center</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-text-secondary">
            <p>&copy; 2024 EmailValidator. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApiDocumentation; 